import ArrowDown from "@/Icons/ArrowDown";
import { useTrans } from "@/translations";

import { useMemo } from "react";

import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { twMerge } from "tailwind-merge";

import { Card, CardComponentFilterLocation } from "../card";
import { useFilterSort } from "./FilterSortContext";
import { FilterSortMenu } from "./FilterSortMenu";
import { FilterSort } from "./types";

type FilterSortButtonProps = FilterSort &
  Pick<React.ComponentProps<typeof Card>, "filterLocation">;

export const FilterSortButton: React.FC<FilterSortButtonProps> = (props) => {
  const { t } = useTrans();
  const { activeOptions } = useFilterSort();

  const activeOptionsForFilterSort = useMemo(
    () => activeOptions[props.id] || [],
    [activeOptions, props.id],
  );

  const optionsToRender = useMemo(
    () =>
      (activeOptionsForFilterSort.filter((value) => !!value.option).length
        ? activeOptionsForFilterSort
        : props.options.filter(
            ({ value }) => value === props.defaultValue || value === "",
          )
      )
        .map(({ option, translate }) => {
          if (option === "interval.custom" || !option)
            return t("genericLabels:custom");

          return translate ? t(option) : option;
        })
        .join(", "),
    [activeOptionsForFilterSort, props.defaultValue, props.options, t],
  );

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <PopoverButton
            className={twMerge(
              "bg-grey1 hover:bg-grey2 mb-2 flex h-8 items-center gap-2 px-2 py-1 text-lg",
              open && "bg-grey2",
            )}
          >
            {optionsToRender ? (
              <div className="text-sm text-ellipsis whitespace-nowrap">
                {optionsToRender}
              </div>
            ) : (
              <div className="text-sm font-medium italic">
                {props.type === "filter"
                  ? t("filterLabels:selectFilter")
                  : t("filterLabels:selectSort")}
              </div>
            )}
            <ArrowDown
              className={twMerge(
                "overflow-visible",
                open && "rotate-180 transition-transform",
              )}
            />
          </PopoverButton>
          <PopoverPanel
            className={twMerge(
              "absolute z-20",
              props.filterLocation === CardComponentFilterLocation.RIGHT &&
                "right-0",
            )}
          >
            <FilterSortMenu
              close={close}
              filterSorts={[{ ...props }]}
              initialFilterSortOpenId={props.id}
            />
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};
